import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { VisibilityState } from '@tanstack/react-table';
import * as React from 'react';
import { createPortal } from 'react-dom';

import { Box } from '../../components/ui/box';
import { Button } from '../../components/ui/button';
import { Icon } from '../../components/ui/icon';
import {
  Popover,
  PopoverPopup,
  PopoverTrigger,
} from '../../components/ui/popover';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Text } from '../../components/ui/text';
import {
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { useIsMobile } from '../../hooks/use-media-query';
import { useIsTouchDevice } from '../../hooks/use-is-touch-device';
import { DND_OVERLAY_PROPS, DND_TOUCH_ACTIVATION } from '../../lib/dnd-attrs';
import { SPRING_DROP_ANIMATION } from '../../lib/drop-settle';

import { DataTableCellContent } from './data-table-cell';
import {
  ColumnRow,
  LeadingGutter,
  SortableColumnRow,
  type ColumnEntry,
} from './data-table-column-settings';
import type { DataTableColumnDef } from './data-table-types';
import { getColumnDefId, getColumnLabel } from './data-table-utils';

// ---------------------------------------------------------------------------
// DataTable column picker — the circle-plus header on the actions column
// ---------------------------------------------------------------------------
//
// Enabled by DataTable's `onColumnVisibilityChange`: the structural actions
// column (which also hosts the per-row "…" menu) gets a circle-plus header
// button opening a single "Columns" list (the reference columns popover) —
// every column in table display order, each row a drag handle + label +
// Switch.
// Toggles apply immediately and the popover stays open; with
// `onColumnOrderChange` also wired, dragging a row reorders the columns
// (up/down buttons replace the drag on touch devices).
//
// Wired through context rather than column-def props: the actions column def
// is created before the table resolves its merged order/visibility, and a
// def rebuilt per visibility change would churn TanStack's column identity.
// The provider (mounted by DataTable) computes the picker model where those
// merges live; the header slot just renders it — and renders nothing when
// the table didn't opt in.
//
// Pinned and `enableHiding: false` columns render as locked rows: switch on
// and disabled, lock glyph in the drag gutter — the same treatment as
// DataTableColumnSettings.

// The list stays reachable without growing past the viewport when a table
// carries many columns.
const LIST_MAX_HEIGHT = 384;

type DataTableColumnPickerValue = {
  /** Locked rows (pinned / unhideable), in table display order. */
  lockedEntries: ColumnEntry[];
  /** Toggleable rows — visible AND hidden interleaved, in display order. */
  entries: ColumnEntry[];
  /** Count of ALL visible consumer columns — guards hiding the last one. */
  visibleCount: number;
  columnVisibility: VisibilityState;
  toggle: (id: string, visible: boolean) => void;
  /** Move `entries[fromIndex]` to `toIndex`. Absent when the table wired no
   *  `onColumnOrderChange` — the rows then render without drag affordances. */
  move?: ((fromIndex: number, toIndex: number) => void) | undefined;
  /** A column resize is in progress — the picker closes for it. */
  resizing: boolean;
};

const DataTableColumnPickerContext =
  React.createContext<DataTableColumnPickerValue | null>(null);

/** Consumer-supplied header node — replaces the built-in trigger entirely
 *  (see DataTable's `addColumnHeader`). */
const DataTableAddColumnHeaderContext =
  React.createContext<React.ReactNode>(null);

export function DataTableColumnPickerProvider<TData>({
  columns,
  columnOrder,
  columnVisibility,
  pinnedColumnIds,
  onColumnVisibilityChange,
  onColumnOrderChange,
  addColumnHeader = null,
  resizing = false,
  children,
}: {
  /** Consumer column defs (structural columns excluded). */
  columns: DataTableColumnDef<TData>[];
  /** The table's fully merged consumer display order. */
  columnOrder: string[];
  columnVisibility: VisibilityState;
  pinnedColumnIds: string[];
  /** Absent → picker disabled; the header slot renders nothing. */
  onColumnVisibilityChange?:
    | ((visibility: VisibilityState) => void)
    | undefined;
  /** Absent → the list renders without reorder affordances. */
  onColumnOrderChange?: ((order: string[]) => void) | undefined;
  /** Consumer header node — see DataTable's prop of the same name. */
  addColumnHeader?: React.ReactNode;
  resizing?: boolean;
  children: React.ReactNode;
}) {
  const value = React.useMemo<DataTableColumnPickerValue | null>(() => {
    if (!onColumnVisibilityChange) return null;
    const byId = new Map<string, DataTableColumnDef<TData>>();
    for (const def of columns) {
      const id = getColumnDefId(def);
      if (id !== undefined) byId.set(id, def);
    }
    const pinnedSet = new Set(pinnedColumnIds);
    const lockedEntries: ColumnEntry[] = [];
    const entries: ColumnEntry[] = [];
    let visibleCount = 0;
    // The merged order preserves ids for columns not currently rendered
    // (gated columns keep their saved slots) — skip those, list the rest.
    for (const id of columnOrder) {
      const def = byId.get(id);
      if (!def) continue;
      if (columnVisibility[id] !== false) visibleCount += 1;
      const locked = def.enableHiding === false || pinnedSet.has(id);
      (locked ? lockedEntries : entries).push({
        id,
        label: getColumnLabel(def, id),
        canHide: !locked,
      });
    }
    const toggle = (id: string, visible: boolean) => {
      // Never allow hiding the last visible column — a zero-column table
      // renders data rows with no cells and persists that way.
      if (!visible && visibleCount <= 1) return;
      // Showing writes visibility ONLY — never the order. The list interleaves
      // hidden rows at their saved slots in the merged order, so the column
      // reappears exactly where its row sits in this popover (a row that
      // jumped to the bottom on toggle would contradict the list). A column
      // with no saved slot is appended by the order merge itself.
      onColumnVisibilityChange({ ...columnVisibility, [id]: visible });
    };
    // Move within the toggleable rows, then rebuild the full order: the
    // moved rows take the new sequence while locked and unrendered ids keep
    // their stored slots (the DataTableColumnSettings `moveShown` recipe).
    const move = onColumnOrderChange
      ? (fromIndex: number, toIndex: number) => {
          const ids = entries.map((e) => e.id);
          const moved = ids.splice(fromIndex, 1)[0];
          if (moved === undefined) return;
          ids.splice(toIndex, 0, moved);
          const movedSet = new Set(ids);
          let cursor = 0;
          const next = columnOrder.map((id) =>
            movedSet.has(id) ? ids[cursor++]! : id,
          );
          onColumnOrderChange(next);
        }
      : undefined;
    return { lockedEntries, entries, visibleCount, columnVisibility, toggle, move, resizing };
  }, [
    resizing,
    columns,
    columnOrder,
    columnVisibility,
    pinnedColumnIds,
    onColumnVisibilityChange,
    onColumnOrderChange,
  ]);

  return (
    <DataTableColumnPickerContext.Provider value={value}>
      <DataTableAddColumnHeaderContext.Provider value={addColumnHeader}>
        {children}
      </DataTableAddColumnHeaderContext.Provider>
    </DataTableColumnPickerContext.Provider>
  );
}

/** The single reorderable toggle list inside the picker popover. */
function ColumnPickerList({ picker }: { picker: DataTableColumnPickerValue }) {
  const isMobile = useIsMobile();
  // Up/down buttons replace the drag list on mobile viewports and on ANY
  // touch device — a wide touch tablet gets working buttons, not a
  // pointer-shaped drag list.
  const isTouch = useIsTouchDevice();
  const useReorderButtons = isMobile || isTouch;

  const [activeId, setActiveId] = React.useState<string | null>(null);

  const { lockedEntries, entries, visibleCount, columnVisibility, toggle, move } =
    picker;

  const entryIds = React.useMemo(() => entries.map((e) => e.id), [entries]);

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: { distance: 8 },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: DND_TOUCH_ACTIVATION,
  });
  // Fixed-shape sensor lists — `useSensors` feeds a useMemo deps array, so
  // each call must receive a constant number of sensors (see
  // DataTableColumnSettings for the full story).
  const pointerSensors = useSensors(mouseSensor, touchSensor);
  const noSensors = useSensors();
  const sensors = useReorderButtons ? noSensors : pointerSensors;

  const handleDragStart = React.useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  }, []);

  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      setActiveId(null);
      const { active, over } = event;
      if (!move || !over || active.id === over.id) return;
      const from = entryIds.indexOf(String(active.id));
      const to = entryIds.indexOf(String(over.id));
      if (from === -1 || to === -1) return;
      move(from, to);
    },
    [entryIds, move],
  );

  const handleDragCancel = React.useCallback(() => setActiveId(null), []);

  const activeEntry = React.useMemo(
    () =>
      activeId !== null
        ? (entries.find((e) => e.id === activeId) ?? null)
        : null,
    [activeId, entries],
  );

  const isVisible = (id: string) => columnVisibility[id] !== false;
  // Disable the switch (not just ignore the toggle) when this row is the
  // last visible column, so the constraint is visible.
  const isToggleDisabled = (id: string) => isVisible(id) && visibleCount <= 1;

  const lockedRows = lockedEntries.map((entry) => (
    <ColumnRow
      key={entry.id}
      asListItem
      entry={entry}
      visible={isVisible(entry.id)}
      onToggleVisibility={toggle}
      leading={
        <LeadingGutter>
          <Icon name="lock" size="sm" color="muted" />
        </LeadingGutter>
      }
    />
  ));

  if (!move) {
    return (
      <>
        {lockedRows}
        {entries.map((entry) => (
          <ColumnRow
            key={entry.id}
            asListItem
            entry={entry}
            visible={isVisible(entry.id)}
            toggleDisabled={isToggleDisabled(entry.id)}
            onToggleVisibility={toggle}
            leading={<LeadingGutter />}
          />
        ))}
      </>
    );
  }

  if (useReorderButtons) {
    return (
      <>
        {lockedRows}
        {entries.map((entry, index) => (
          <ColumnRow
            key={entry.id}
            asListItem
            entry={entry}
            visible={isVisible(entry.id)}
            toggleDisabled={isToggleDisabled(entry.id)}
            onToggleVisibility={toggle}
            leading={
              <Box direction="row">
                <Button
                  variant="ghost"
                  size="icon-xs"
                  shape="square"
                  disabled={index === 0}
                  onClick={() => move(index, index - 1)}
                  aria-label={`Move ${entry.label} up`}
                >
                  <Icon name="chevron-up" size="sm" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-xs"
                  shape="square"
                  disabled={index === entries.length - 1}
                  onClick={() => move(index, index + 1)}
                  aria-label={`Move ${entry.label} down`}
                >
                  <Icon name="chevron-down" size="sm" />
                </Button>
              </Box>
            }
          />
        ))}
      </>
    );
  }

  return (
    <>
      {lockedRows}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext items={entryIds} strategy={verticalListSortingStrategy}>
          {entries.map((entry) => (
            <SortableColumnRow
              key={entry.id}
              persistentGrip
              entry={entry}
              visible={isVisible(entry.id)}
              toggleDisabled={isToggleDisabled(entry.id)}
              onToggleVisibility={toggle}
            />
          ))}
        </SortableContext>
        {/* DragOverlay does NOT portal itself — it positions with `fixed`,
            and the popover popup is transform-positioned, which makes a fixed
            descendant position relative to the popup instead of the viewport.
            Portal it to body. The overlay marker attributes go on the CHILD —
            DragOverlay drops unknown props (see ../../lib/dnd-attrs.ts). */}
        {createPortal(
          <DragOverlay dropAnimation={SPRING_DROP_ANIMATION}>
            {activeEntry ? (
              <Box surface="popover" rounded="md" border {...DND_OVERLAY_PROPS}>
                <ColumnRow
                  entry={activeEntry}
                  visible={isVisible(activeEntry.id)}
                  onToggleVisibility={toggle}
                  leading={
                    <LeadingGutter>
                      <Icon name="grip-vertical" size="sm" color="muted" />
                    </LeadingGutter>
                  }
                />
              </Box>
            ) : null}
          </DragOverlay>,
          document.body,
        )}
      </DndContext>
    </>
  );
}

/**
 * Hide control for one column, read off the shared picker model — the header
 * context menu's "Hide column" item goes through the same toggle the
 * circle-plus picker and DataTableColumnSettings use, so every visibility
 * edit lands in the consumer's one `onColumnVisibilityChange`. Null when the
 * table has no visibility handler or the column is locked (pinned /
 * `enableHiding: false`); `disabled` guards hiding the last visible column.
 */
export function useDataTableColumnHide(
  columnId: string,
): { hide: () => void; disabled: boolean } | null {
  const picker = React.useContext(DataTableColumnPickerContext);
  if (!picker) return null;
  const entry = picker.entries.find((e) => e.id === columnId);
  if (!entry || !entry.canHide) return null;
  return {
    hide: () => picker.toggle(columnId, false),
    disabled: picker.visibleCount <= 1,
  };
}

/**
 * Header slot for the structural actions column. Renders the circle-plus
 * trigger when the table opted into the picker, nothing otherwise.
 */
export function DataTableAddColumnHeader() {
  const picker = React.useContext(DataTableColumnPickerContext);
  const customHeader = React.useContext(DataTableAddColumnHeaderContext);
  const [open, setOpen] = React.useState(false);
  // The resize seam stops its press from propagating, so the popover's
  // outside-press never sees it — close for the drag explicitly.
  const resizing = picker?.resizing ?? false;
  React.useEffect(() => {
    if (resizing) setOpen(false);
  }, [resizing]);
  // A consumer-supplied node replaces the built-in trigger entirely —
  // surfaces whose column controls live in their own popover anchor it here
  // so the header affordance opens the same panel as their toolbar action.
  if (customHeader != null) {
    return (
      <DataTableCellContent align="end">{customHeader}</DataTableCellContent>
    );
  }
  if (
    !picker ||
    (picker.lockedEntries.length === 0 && picker.entries.length === 0)
  ) {
    return null;
  }

  return (
    <DataTableCellContent align="end">
      <Popover open={open} onOpenChange={setOpen}>
        {/* The TooltipTrigger sits INSIDE the popover trigger's render chain
            so its hover handlers merge onto the button element itself (the
            DataTableColumnSettings gear's recipe). */}
        <Tooltip>
          <PopoverTrigger
            render={
              <TooltipTrigger
                render={
                  <Button variant="ghost" size="icon-xs" aria-label="Add" />
                }
              />
            }
          >
            {/* The glyph matches the column-header text exactly — the header's
                faint step, not the button's own content color. */}
            <Icon name="circle-plus" className="text-faint-foreground" />
          </PopoverTrigger>
          {/* Left, not top: the trigger hugs the table's right edge under the
              sticky header, so a top popup crowds the header line while the
              open space is beside the button. */}
          <TooltipPopup side="left">Add</TooltipPopup>
        </Tooltip>
        <PopoverPopup align="end" padding="sm" className="w-64">
          <Box paddingX="sm" paddingTop="xs" paddingBottom="xs">
            <Text size="xs" variant="faint" weight="medium">
              Columns
            </Text>
          </Box>
          <ScrollArea style={{ maxHeight: LIST_MAX_HEIGHT }}>
            <Box role="list" aria-label="Columns">
              <ColumnPickerList picker={picker} />
            </Box>
          </ScrollArea>
        </PopoverPopup>
      </Popover>
    </DataTableCellContent>
  );
}

DataTableColumnPickerProvider.displayName = 'DataTableColumnPickerProvider';
DataTableAddColumnHeader.displayName = 'DataTableAddColumnHeader';
